import type { ArtikelVorschauAntwort } from '../lib/typen';
import { zahl } from '../lib/format';

/**
 * Was ein Mensch entscheiden muss, bevor geschrieben werden kann.
 *
 * Zwei Listen, beide bewusst als Arbeitsliste und nicht als Fehlermeldung:
 *
 *   **Ungemappte Dateikategorien** — das Tool legt NIE eine Kategorie in Plenty an
 *   (Leitplanke 2). Diese Artikel entstehen ohne Kategorie, wenn niemand eine Zuordnung
 *   pflegt. Das ist erlaubt, aber es sollte eine Entscheidung sein, kein Versehen.
 *
 *   **Fehlende Attributwerte** — ohne sie kippt die Variantenanlage. Sie werden
 *   vorgeschlagen, nie still angelegt: ein Attributwert ist ein Stammdatensatz, den
 *   danach der ganze Katalog sieht.
 */
export default function OffeneArbeit({ daten }: { daten: ArtikelVorschauAntwort }) {
  const { kategorien, attribute } = daten;
  const hatUngemappt = kategorien.ungemappt.length > 0;
  const hatFehlendeWerte = attribute.fehlendGesamt > 0;
  const hatMeldungen = kategorien.meldungen.length > 0 || attribute.meldungen.length > 0;

  if (!hatUngemappt && !hatFehlendeWerte && !hatMeldungen) return null;

  return (
    <div className="space-y-4 rounded border border-amber-300 bg-amber-50/50 p-4">
      <h3 className="font-semibold text-[#1A1A1A]">Das muss noch jemand entscheiden</h3>

      {kategorien.meldungen.map((text, i) => (
        <p key={`km-${i}`} className="text-sm text-amber-900">{text}</p>
      ))}

      {hatUngemappt && (
        <div className="space-y-2">
          <p className="text-sm text-[#1A1A1A]/80">
            <strong>{zahl(kategorien.ungemappt.length)} Dateikategorien ohne Zuordnung</strong>{' '}
            — die betroffenen {zahl(kategorien.ohneKategorie)} Artikel würden ohne
            Kategorie angelegt. Das Tool legt selbst keine Kategorie in Plenty an.
          </p>
          <div className="flex flex-wrap gap-2">
            {kategorien.ungemappt.slice(0, 30).map(eintrag => (
              <span
                key={eintrag.wert}
                className="rounded border border-[#DAD9D9] bg-white px-2 py-1 text-xs"
              >
                {eintrag.wert}
                <span className="ml-1.5 text-[#1A1A1A]/50">{zahl(eintrag.artikel)}</span>
              </span>
            ))}
            {kategorien.ungemappt.length > 30 && (
              <span className="px-2 py-1 text-xs text-[#1A1A1A]/60">
                … und {zahl(kategorien.ungemappt.length - 30)} weitere
              </span>
            )}
          </div>
        </div>
      )}

      {attribute.meldungen.map((text, i) => (
        <p key={`am-${i}`} className="text-sm text-amber-900">{text}</p>
      ))}

      {hatFehlendeWerte && (
        <div className="space-y-2">
          {attribute.befunde
            .filter(b => b.fehlend.length > 0)
            .map(befund => (
              <div key={befund.attributId} className="space-y-1">
                <p className="text-sm text-[#1A1A1A]/80">
                  <strong>{befund.attributName}</strong>: {zahl(befund.fehlend.length)} Werte
                  fehlen in Plenty
                </p>
                <div className="flex flex-wrap gap-2">
                  {befund.fehlend.slice(0, 30).map(f => (
                    <span
                      key={f.wert}
                      className="rounded border border-[#DAD9D9] bg-white px-2 py-1 text-xs"
                    >
                      {f.wert}
                      {f.anzahl > 0 && (
                        <span className="ml-1.5 text-[#1A1A1A]/50">{zahl(f.anzahl)}×</span>
                      )}
                    </span>
                  ))}
                  {befund.fehlend.length > 30 && (
                    <span className="px-2 py-1 text-xs text-[#1A1A1A]/60">
                      … und {zahl(befund.fehlend.length - 30)} weitere
                    </span>
                  )}
                </div>
              </div>
            ))}
          <p className="text-xs text-[#1A1A1A]/60">
            Diese Werte werden nicht automatisch angelegt. Ein Attributwert ist ein
            Stammdatensatz, den danach der ganze Katalog sieht — die Anlage läuft später
            über eine Sammelbestätigung.
          </p>
        </div>
      )}
    </div>
  );
}
